import React from 'react';

/* The live app is heavy, so it starts as a picture; tapping loads the real thing in place. */
const LiveOnTap = () => {
  const [on, setOn] = React.useState(false);
  return (
    <div style={{
      position: 'relative', width: '100%', height: on ? '80vh' : 'auto', minHeight: on ? '600px' : 0,
      background: '#000', borderRadius: '20px', overflow: 'hidden',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)', border: '2px solid var(--border-color)'
    }}>
      {on ? (
        <iframe src="https://www.instash.app" title="Stash, live" style={{ width: '100%', height: '100%', border: 'none' }}
          allow="clipboard-write; encrypted-media; picture-in-picture" />
      ) : (
        <button onClick={() => setOn(true)} aria-label="Open Stash live" style={{
          display: 'block', width: '100%', padding: 0, border: 0, background: 'none', cursor: 'pointer', position: 'relative'
        }}>
          <img src="/portfolio-screens/stash/desktop-2.png" alt="Stash on desktop" loading="lazy" style={{ width: '100%', display: 'block' }} />
          <span style={{
            position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
            padding: '14px 26px', borderRadius: '999px', fontWeight: 700, fontSize: '15px', color: '#fff',
            background: 'linear-gradient(135deg, #f87171 0%, #e11d48 100%)', boxShadow: '0 12px 30px rgba(225,29,72,0.45)'
          }}>Try it live →</span>
        </button>
      )}
    </div>
  );
};

export const stashContent = {
  pageMaxWidth: '1200px',
  breadcrumb: {
    text: 'Back to Home',
    url: '/'
  },

  sections: [
    {
      id: 'hero',
      spacing: 'clamp(32px, 5vw, 64px)',
      blocks: [
        {
          type: 'container',
          narrow: true,
          blocks: [
            {
              type: 'custom',
              render: () => (
                <span style={{
                  background: 'linear-gradient(135deg, #f87171 0%, #e11d48 100%)',
                  color: 'white',
                  padding: '8px 20px',
                  borderRadius: '25px',
                  fontSize: '13px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'inline-block'
                }}>
                  Web & Mobile App
                </span>
              )
            }
          ]
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'custom',
          render: () => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <img
                src="/stash-logo.png"
                alt="Stash Logo"
                style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(248, 113, 113, 0.3)'
                }}
              />
              <h1 style={{
                color: 'var(--text-primary)',
                fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                fontWeight: '700',
                margin: 0
              }}>
                Stash
              </h1>
            </div>
          )
        },
        {
          type: 'text',
          content: 'Save anything. Find everything.',
          size: 'large'
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'text',
          content: 'A Progressive Web App for saving, organizing, and revisiting content from TikTok, Instagram, YouTube, Twitter/X, and any URL. Paste a link and Stash automatically extracts metadata and organizes everything into searchable collections.'
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'custom',
          render: () => (
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://www.instash.app"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: 'linear-gradient(135deg, #f87171 0%, #e11d48 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Visit Live Site
              </a>
              <span style={{
                background: 'var(--bg-accent)',
                color: 'var(--text-accent)',
                padding: '12px 24px',
                borderRadius: '8px',
                fontWeight: '600'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-label="Waiting" style={{ verticalAlign: '-5px', marginRight: '8px' }}>
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>App Store
              </span>
            </div>
          )
        }
      ]
    },

    // LIVE EMBED
    {
      id: 'live-demo',
      title: 'Try Stash Live',
      blocks: [
        {
          type: 'text',
          content: 'Save content from any platform, organize it your way, and build your personal content library.',
          size: 'large'
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'custom',
          render: () => <LiveOnTap />
        }
      ]
    },

    // APP SCREENSHOTS - MOBILE
    {
      id: 'screenshots-mobile',
      title: 'Inside the app',
      blocks: [
        {
          type: 'custom',
          render: () => {
            const screens = [
              { src: '/portfolio-screens/stash/1.png', label: 'Home Dashboard' },
              { src: '/portfolio-screens/stash/2.png', label: 'Platform Filters' },
              { src: '/portfolio-screens/stash/3.png', label: 'Share to Stash' },
              { src: '/portfolio-screens/stash/4.png', label: 'Video Details' },
              { src: '/portfolio-screens/stash/5.png', label: 'Pinterest Filter' },
              { src: '/portfolio-screens/stash/6.png', label: 'Twitter/X Filter' },
              { src: '/portfolio-screens/stash/7.png', label: 'Instagram Filter' },
              { src: '/portfolio-screens/stash/8.png', label: 'Sidebar Navigation' }
            ];
            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                maxWidth: '500px',
                margin: '0 auto'
              }}>
                {screens.map((screen, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <img
                      src={screen.src}
                      alt={screen.label}
                      style={{
                        width: '100%',
                        borderRadius: '16px',
                        border: '1px solid var(--border-color)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)'
                      }}
                    />
                    <p style={{
                      color: 'var(--text-secondary)',
                      fontSize: '0.75rem',
                      marginTop: '8px'
                    }}>
                      {screen.label}
                    </p>
                  </div>
                ))}
              </div>
            );
          }
        }
      ]
    },

    // DESKTOP SCREENSHOTS
    {
      id: 'screenshots-desktop',
      title: 'Desktop Version',
      blocks: [
        {
          type: 'custom',
          render: () => {
            const screens = [
              { src: '/portfolio-screens/stash/desktop-1.png', label: 'Desktop Dashboard' },
              { src: '/portfolio-screens/stash/desktop-2.png', label: 'Quick Save Modal' }
            ];
            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px'
              }}>
                {screens.map((screen, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <img
                      src={screen.src}
                      alt={screen.label}
                      style={{
                        width: '100%',
                        borderRadius: '16px',
                        border: '1px solid var(--border-color)',
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)'
                      }}
                    />
                    <p style={{
                      color: 'var(--text-secondary)',
                      fontSize: '0.85rem',
                      marginTop: '10px'
                    }}>
                      {screen.label}
                    </p>
                  </div>
                ))}
              </div>
            );
          }
        }
      ]
    },

    // FEATURES
    {
      id: 'features',
      title: 'Features',
      blocks: [
        {
          type: 'custom',
          render: () => {
            const features = [
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
                  </svg>
                ),
                title: 'Save From Anywhere',
                desc: 'Paste any URL from TikTok, Instagram, YouTube, Twitter/X, or any website — Stash handles the rest'
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/>
                  </svg>
                ),
                title: 'Multi-Platform Support',
                desc: 'Works with TikTok, Instagram, YouTube, Twitter/X, and any generic URL out of the box'
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                  </svg>
                ),
                title: 'Collections',
                desc: 'Organize your saved content into custom collections, mark favorites, and archive items'
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                ),
                title: 'Smart Search',
                desc: 'Find anything you saved — search by title, creator, platform, or content type'
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                ),
                title: 'Auto Metadata',
                desc: 'Automatically extracts thumbnails, titles, creators, and descriptions from saved URLs'
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
                  </svg>
                ),
                title: 'PWA + Native Apps',
                desc: 'Install as a PWA on any device, or use the native iOS app built with Capacitor'
              }
            ];
            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '24px'
              }}>
                {features.map((feature, i) => (
                  <div key={i} style={{
                    background: 'var(--bg-secondary)',
                    padding: '24px',
                    borderRadius: '16px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(248, 113, 113, 0.12) 0%, rgba(225, 29, 72, 0.12) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px',
                      color: '#e11d48'
                    }}>
                      {feature.icon}
                    </div>
                    <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px', fontSize: '1.1rem' }}>{feature.title}</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', margin: 0, lineHeight: '1.5' }}>{feature.desc}</p>
                  </div>
                ))}
              </div>
            );
          }
        }
      ]
    },

    // TECH STACK
    {
      id: 'tech',
      title: 'Tech Stack',
      blocks: [
        {
          type: 'custom',
          render: () => (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '16px'
            }}>
              {[
                { category: 'Frontend', items: ['Next.js 14', 'TypeScript', 'Tailwind CSS', 'Framer Motion'] },
                { category: 'Backend & Data', items: ['Neon PostgreSQL', 'Drizzle ORM', 'NextAuth.js', 'Supabase'] },
                { category: 'APIs & Services', items: ['Metadata Extraction', 'URL Parsing', 'Lemonsqueezy', 'Push Notifications'] },
                { category: 'Mobile & PWA', items: ['Capacitor (iOS)', 'Service Worker', 'Share Target', 'Push Notifications'] }
              ].map((group, i) => (
                <div key={i} style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  padding: '20px'
                }}>
                  <h4 style={{
                    color: 'var(--text-accent)',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '12px'
                  }}>
                    {group.category}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {group.items.map((item, j) => (
                      <span key={j} style={{
                        color: 'var(--text-secondary)',
                        fontSize: '0.9rem'
                      }}>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        }
      ]
    },

    // HOW IT WAS BUILT
    {
      id: 'process',
      title: 'How It Was Built',
      blocks: [
        {
          type: 'container',
          background: true,
          blocks: [
            {
              type: 'text',
              content: 'Instead of losing bookmarks across apps, Stash gives you one place for everything. Paste a link and it automatically pulls the thumbnail, title, and creator info so you can find things later without remembering exactly where you saved them.',
              size: 'normal',
              align: 'left'
            },
            { type: 'spacer', size: 'small' },
            {
              type: 'custom',
              render: () => (
                <ul style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  paddingLeft: '20px',
                  margin: 0
                }}>
                  <li>Next.js 14 with App Router for server-side rendering and API routes</li>
                  <li>Neon serverless PostgreSQL with Drizzle ORM for type-safe database access</li>
                  <li>NextAuth.js for email/password and Google OAuth authentication</li>
                  <li>Automatic metadata extraction for thumbnails, titles, and creator info</li>
                  <li>Capacitor wraps the web app into a native iOS app</li>
                  <li>PWA with service worker for offline support and share target API</li>
                  <li>Lemonsqueezy integration for payment processing</li>
                </ul>
              )
            }
          ]
        }
      ]
    }
  ],

  callToAction: {
    brand: { bg: "linear-gradient(135deg, #E11D48 0%, #F87171 100%)", fg: "#FFFFFF", btnBg: "#FFFFFF", btnFg: "#BE123C" , logo: "/stash-logo.png", logoWidth: 72, logoRadius: 18 },
    title: 'Stash',
    text: 'Save anything. Find everything.',
    button: { text: 'Try Stash →', url: 'https://www.instash.app' }
  }
};